import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cmsService } from '../../services/cmsService';
import SafeIcon from '../../common/SafeIcon';
import { FiChevronDown, FiMapPin } from 'react-icons/fi';

const LocationSEO = () => {
  const [locations, setLocations] = useState([]);
  const [openState, setOpenState] = useState(null);
  const [openGroup, setOpenGroup] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadLocations = async () => {
      const data = await cmsService.fetchData();
      setLocations(data.locations || []);
      setLoading(false);
    };
    loadLocations();
  }, []);

  const toggleState = (idx) => {
    setOpenState(openState === idx ? null : idx);
    setOpenGroup(null);
  };

  const toggleGroup = (groupIdx) => {
    setOpenGroup(openGroup === groupIdx ? null : groupIdx);
  };

  const StaticSeoCopy = () => (
    <p className="text-xs font-light text-soft/60 leading-relaxed max-w-2xl mx-auto text-balance">
      BPA Solutions is based in Sydney and creates premium jewellery ecommerce visuals for brands across Australia, including Sydney, Melbourne, Brisbane, Gold Coast, Perth, Adelaide, Canberra and Hobart. The service is designed for ecommerce stores that need model-wearing jewellery images, product page visuals, campaign assets and short try-on style videos without organising a studio shoot.
    </p>
  );

  // Defensive render - only show if we have valid grouped data
  if (loading) return null;

  // Compact fallback if no locations loaded
  if (!locations || locations.length === 0) {
    return (
      <section className="bg-espressoDeep text-soft border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
          <div className="text-center">
            <p className="text-[10px] font-bold text-muted uppercase tracking-widest mb-3">Areas We Cater To</p>
            <StaticSeoCopy />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-espressoDeep text-soft border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl text-gold mb-3 font-serif tracking-wide">Areas We Cater To</h2>
            <StaticSeoCopy />
          </div>

          {locations.length > 0 ? (
            <div className="space-y-1">
              {locations.map((stateData, sIdx) => (
                <div key={sIdx} className="border-b border-white/5 last:border-0 overflow-hidden">
                  <button
                    onClick={() => toggleState(sIdx)}
                    className="w-full py-4 flex justify-between items-center text-left group hover:bg-white/5 px-2 rounded-sm transition-colors"
                    aria-expanded={openState === sIdx}
                  >
                    <div className="flex items-center gap-4">
                      <span className="text-sm font-serif text-soft group-hover:text-gold transition-colors tracking-wide">
                        {stateData.state}
                      </span>
                    </div>
                    <SafeIcon
                      icon={FiChevronDown}
                      className={`w-4 h-4 text-gold transition-transform duration-500 ${
                        openState === sIdx ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  <AnimatePresence>
                    {openState === sIdx && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="pl-4 pr-2 pb-4 space-y-2">
                          {stateData.displayGroups?.map((group, gIdx) => (
                            <div key={gIdx} className="border border-white/5 bg-white/5 rounded-sm">
                              <button
                                onClick={() => toggleGroup(`${sIdx}-${gIdx}`)}
                                className="w-full px-4 py-3 flex justify-between items-center text-left text-xs hover:bg-white/5 transition-colors"
                              >
                                <span className="uppercase tracking-widest text-gold/80 font-medium">
                                  {group.groupName}
                                </span>
                                <SafeIcon
                                  icon={FiChevronDown}
                                  className={`w-3 h-3 text-soft/40 transition-transform ${
                                    openGroup === `${sIdx}-${gIdx}` ? 'rotate-180' : ''
                                  }`}
                                />
                              </button>
                              <AnimatePresence>
                                {openGroup === `${sIdx}-${gIdx}` && (
                                  <motion.div
                                    initial={{ height: 0 }}
                                    animate={{ height: 'auto' }}
                                    exit={{ height: 0 }}
                                    className="overflow-hidden"
                                  >
                                    <div className="px-4 pb-4 pt-1">
                                      <div className="flex flex-wrap gap-x-4 gap-y-2">
                                        {group.suburbs?.map((suburb, subIdx) => (
                                          <div
                                            key={subIdx}
                                            className="flex items-center gap-1.5 text-soft/50 hover:text-gold transition-colors cursor-default"
                                          >
                                            <SafeIcon icon={FiMapPin} className="w-2.5 h-2.5 text-gold/50" />
                                            <span className="text-[10px] font-light">{suburb}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 border border-white/5 bg-white/5 rounded-soft">
              <p className="text-soft/40 font-light text-xs italic">Currently serving premium brands Australia-wide.</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default LocationSEO;